import { z } from 'zod';
const short = z.string().trim().max(150);
const website = z
  .string()
  .trim()
  .max(2048)
  .refine((v) => {
    if (!v) return true;
    try {
      const u = new URL(v);
      return u.protocol === 'https:' && !u.username && !u.password;
    } catch {
      return false;
    }
  }, 'Enter an HTTPS URL');
export const SetupProfileSchema = z
  .object({
    businessName: short,
    industry: short,
    website,
    market: short,
    timeZone: z
      .string()
      .max(80)
      .refine((v) => {
        try {
          new Intl.DateTimeFormat('en', { timeZone: v });
          return true;
        } catch {
          return false;
        }
      }),
    languages: z
      .array(z.enum(['en', 'ar']))
      .min(1)
      .max(2),
  })
  .strict();
export const SetupInputSchema = z
  .object({
    revision: z.number().int().nonnegative(),
    mode: z.enum(['guided', 'manual']),
    step: z.enum(['welcome', 'business', 'goals', 'accounts', 'brief', 'review']),
    profile: SetupProfileSchema,
    goals: z
      .array(z.enum(['leads', 'bookings', 'sales', 'visibility', 'retention']))
      .max(2)
      .refine((v) => new Set(v).size === v.length),
    manualSources: z
      .array(z.object({ name: short, url: website, notes: z.string().max(2000) }).strict())
      .max(12),
    brief: z
      .object({
        services: z.string().max(3000),
        audience: z.string().max(1500),
        offer: z.string().max(1500),
        tone: z.string().max(300),
      })
      .strict(),
  })
  .strict();
export type SetupInput = z.infer<typeof SetupInputSchema>;
export interface PhoneStatus {
  available: boolean;
  required: boolean;
  verified: boolean;
  maskedPhone: string | null;
  assistanceRequested: boolean;
  challengeId: string | null;
  expiresAt: number | null;
  resendAt: number | null;
}
export interface SetupView extends Omit<SetupInput, 'mode'> {
  mode: 'guided' | 'manual' | null;
  completedAt: string | null;
  shouldPrompt: boolean;
  phone: PhoneStatus;
}
export function emptySetup(businessName = ''): SetupInput {
  return {
    revision: 0,
    mode: 'guided',
    step: 'welcome',
    profile: {
      businessName,
      industry: '',
      website: '',
      market: 'AE',
      timeZone: 'Asia/Dubai',
      languages: ['en'],
    },
    goals: [],
    manualSources: [],
    brief: { services: '', audience: '', offer: '', tone: '' },
  };
}
